import { Link } from "react-router-dom";
import { EmptyState, ErrorState, FreshnessBar } from "../components/AdminPageStates";
import { useFounderBrief } from "../hooks/useFounderBrief";
import {
  founderBriefCachedSources,
  FounderBriefAction,
  FounderBriefHealthCard,
  FounderBriefInsight,
  FounderBriefReflection,
  FounderBriefStatus,
  FounderBriefStory,
  FounderBriefTimelineEvent,
  statusLabel
} from "../services/founderBriefService";

const LOADING_HEALTH_CARDS = ["Community", "Revenue", "Growth", "Infrastructure", "Needs Attention"];

function FounderBriefLoadingCard({ title }: { title: string }) {
  return (
    <section className="founder-brief-health-card loading-card" aria-busy="true">
      <div className="founder-brief-card-topline">
        <span className="founder-brief-icon" aria-hidden="true">
          ·
        </span>
        <span className="status-badge watch">Watch</span>
      </div>
      <p className="eyebrow">{title}</p>
      <div className="skeleton skeleton-title" />
      <div className="skeleton skeleton-note" />
    </section>
  );
}

function FounderBriefLoadingPanel() {
  return (
    <div className="founder-brief-story-loading loading-card" aria-busy="true">
      <div className="skeleton skeleton-title" />
      <div className="skeleton skeleton-note" />
    </div>
  );
}

function FounderStoryItem({ story }: { story: FounderBriefStory }) {
  return (
    <li className="founder-brief-story-item">
      <span aria-hidden="true">{story.icon}</span>
      <p>{story.text}</p>
    </li>
  );
}

function FounderHealthCard({ card }: { card: FounderBriefHealthCard }) {
  return (
    <Link className="founder-brief-health-card" to={card.to} aria-label={`${card.title}. ${card.statusLabel}. ${card.value}. ${card.sentence}`}>
      <div className="founder-brief-card-topline">
        <span className="founder-brief-icon" aria-hidden="true">
          {card.icon}
        </span>
        <span className={`status-badge ${card.status}`}>{card.statusLabel}</span>
      </div>
      <div>
        <p className="eyebrow">{card.title}</p>
        <h3>{card.value}</h3>
      </div>
      <p>{card.sentence}</p>
    </Link>
  );
}

function FounderInsightCard({ insight }: { insight: FounderBriefInsight }) {
  return (
    <Link className="founder-brief-insight-card" to={insight.to} aria-label={`${insight.headline} ${insight.insight} ${insight.action}.`}>
      <span className="founder-brief-icon" aria-hidden="true">
        {insight.icon}
      </span>
      <div>
        <h3>{insight.headline}</h3>
        <p>{insight.insight}</p>
      </div>
      <span className="founder-brief-action">{insight.action} →</span>
    </Link>
  );
}

function ActionRow({ action }: { action: FounderBriefAction }) {
  return (
    <Link className="founder-brief-action-row" to={action.to} aria-label={`${action.label} ${action.detail}`}>
      <span className={`status-badge ${action.status}`}>{statusLabel(action.status)}</span>
      <span>
        <strong>{action.label}</strong>
        <small>{action.detail}</small>
      </span>
      <span aria-hidden="true">→</span>
    </Link>
  );
}

function TimelineRow({ event }: { event: FounderBriefTimelineEvent }) {
  return (
    <article className="founder-brief-timeline-row">
      <span className="founder-brief-timeline-icon" aria-hidden="true">
        {event.icon}
      </span>
      <div>
        <h3>{event.title}</h3>
        <p>{event.detail}</p>
        <small>{event.occurredAt.toLocaleDateString(undefined, { month: "short", day: "numeric" })}</small>
      </div>
    </article>
  );
}

function FounderReflection({ reflection }: { reflection: FounderBriefReflection }) {
  return (
    <section className="founder-brief-reflection" aria-label="Founder's Reflection">
      <p className="eyebrow">Founder's Reflection</p>
      <blockquote>{reflection.text}</blockquote>
    </section>
  );
}

function statusToneClass(status: FounderBriefStatus) {
  return `founder-brief-pulse ${status}`;
}

export default function Dashboard() {
  const { brief, error, isLoading, lastUpdated, refresh } = useFounderBrief();

  if (error) {
    return <ErrorState message="Unable to load Founder Brief. Check Firebase permissions and cached admin analytics summaries." />;
  }

  if (!isLoading && !brief) {
    return <EmptyState message="Today was quiet. That's okay. Tomorrow is another chance to help dancers grow." />;
  }

  return (
    <section className="founder-brief-page" aria-label="Founder Command Center">
      <FreshnessBar
        dataSource={`Cached Founder Brief summaries from ${founderBriefCachedSources()}`}
        isLive={false}
        lastUpdated={lastUpdated}
        onRefresh={() => {
          void refresh();
        }}
      />

      <header className="founder-brief-header">
        <div>
          <p className="eyebrow">{brief?.dateLabel ?? "Today's Brief"}</p>
          <h2>{brief ? `${brief.greeting} 👋` : "Good morning, Melissa 👋"}</h2>
        </div>
        <div className="founder-brief-header-actions">
          <Link className="founder-brief-header-link" to="/journal">
            Founder's Journal →
          </Link>
          <Link className="founder-brief-header-link" to="/story">
            Founder Timeline →
          </Link>
        </div>
      </header>

      <section className={statusToneClass(brief?.pulse.status ?? "watch")} aria-label="Founder Pulse">
        <div>
          <p className="eyebrow">Founder Pulse</p>
          <h1>🚀 Founder Pulse</h1>
          <p>{brief?.pulse.summary ?? "Preparing today's company story."}</p>
        </div>
        <span className={`status-badge ${brief?.pulse.status ?? "watch"}`}>{brief?.pulse.statusLabel ?? "Watch"}</span>
      </section>

      <section className="founder-brief-section" aria-labelledby="todays-story-heading">
        <div className="founder-brief-section-header">
          <p className="eyebrow">Today's Story</p>
          <h2 id="todays-story-heading">What happened while you were away?</h2>
        </div>
        {isLoading || !brief ? (
          <FounderBriefLoadingPanel />
        ) : brief.stories.length > 0 ? (
          <ul className="founder-brief-story-list">
            {brief.stories.map((story) => (
              <FounderStoryItem key={story.id} story={story} />
            ))}
          </ul>
        ) : (
          <div className="founder-brief-quiet-card">
            <h3>Today was quiet.</h3>
            <p>That's okay. Tomorrow is another chance to help dancers grow.</p>
          </div>
        )}
      </section>

      <section className="founder-brief-section" aria-labelledby="quick-health-heading">
        <div className="founder-brief-section-header">
          <p className="eyebrow">Quick Health</p>
          <h2 id="quick-health-heading">Five signals. One glance.</h2>
        </div>
        <div className="founder-brief-health-grid">
          {isLoading || !brief
            ? LOADING_HEALTH_CARDS.map((title) => <FounderBriefLoadingCard key={title} title={title} />)
            : brief.healthCards.map((card) => <FounderHealthCard key={card.id} card={card} />)}
        </div>
      </section>

      <section className="founder-brief-section" aria-labelledby="founder-insights-heading">
        <div className="founder-brief-section-header">
          <p className="eyebrow">Three Insights</p>
          <h2 id="founder-insights-heading">Only what matters today.</h2>
        </div>
        <div className="founder-brief-insight-grid">
          {isLoading || !brief ? (
            <FounderBriefLoadingPanel />
          ) : (
            brief.insights.slice(0, 3).map((insight) => <FounderInsightCard key={insight.id} insight={insight} />)
          )}
        </div>
      </section>

      <section className="founder-brief-section" aria-labelledby="action-required-heading">
        <div className="founder-brief-section-header">
          <p className="eyebrow">Action Required</p>
          <h2 id="action-required-heading">What needs your attention?</h2>
        </div>
        {isLoading || !brief ? (
          <FounderBriefLoadingPanel />
        ) : brief.actions.length > 0 ? (
          <div className="founder-brief-action-list">
            {brief.actions.map((action) => (
              <ActionRow key={action.id} action={action} />
            ))}
          </div>
        ) : (
          <div className="founder-brief-quiet-card">
            <h3>Everything looks healthy.</h3>
            <p>No action needed today.</p>
          </div>
        )}
      </section>

      <section className="founder-brief-section" aria-labelledby="founder-timeline-heading">
        <div className="founder-brief-section-header">
          <p className="eyebrow">Founder Timeline</p>
          <h2 id="founder-timeline-heading">The story Chor is writing.</h2>
        </div>
        {isLoading || !brief ? (
          <FounderBriefLoadingPanel />
        ) : brief.timeline.length > 0 ? (
          <div className="founder-brief-timeline">
            {brief.timeline.map((event) => (
              <TimelineRow key={event.id} event={event} />
            ))}
          </div>
        ) : (
          <div className="founder-brief-quiet-card">
            <h3>No new milestones yet.</h3>
            <p>When Chor reaches something meaningful, it will appear here.</p>
          </div>
        )}
      </section>

      {isLoading || !brief ? <FounderBriefLoadingPanel /> : <FounderReflection reflection={brief.reflection} />}
    </section>
  );
}
